import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Radio, Play, Loader2, Heart, MessageSquareDot, Music4, Sparkles, Mic, Church, HandHeart, ExternalLink, Flame, Star } from "lucide-react";
import StationCard from "@/components/StationCard";
import ShareApp from "@/components/ShareApp";
import FeaturedEvents from "@/components/FeaturedEvents";
import TrendingEvents from "@/components/TrendingEvents";
import GettingStarted from "@/components/GettingStarted";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { useBrand } from "@/context/BrandContext";
import { toast } from "sonner";
import { LOGO } from "@/lib/brand";
import { HOST, nextLive, formatNextLive } from "@/lib/host";
import { API } from "@/lib/api";
import Countdown from "@/components/Countdown";

// Renders a heading with the highlighted word wrapped in the brand color.
function HeroHeading({ text, highlight, color }) {
  if (!highlight || !text.includes(highlight)) return <>{text}</>;
  const [before, after] = text.split(highlight);
  return <>{before}<span style={{ color }}>{highlight}</span>{after}</>;
}

// Picks the featured host for today's weekday, falling back to the first / default.
function pickHost(hosts) {
  const fallback = { name: HOST.name, role: HOST.role, photo: HOST.photo };
  if (!hosts || !hosts.length) return fallback;
  const today = new Date().getDay();
  return hosts.find((h) => (h.days || []).includes(today)) || hosts[0] || fallback;
}

function resolvePhoto(photo) {
  if (!photo) return HOST.photo;
  return photo.startsWith("http") ? photo : `${API.replace("/api", "")}${photo}`;
}

// True if a scheduled show is currently on air (within a slot's 2-hour window).
const HERO = "https://images.unsplash.com/photo-1570786032462-2efc3ca8fccd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB3b3JzaGlwJTIwY29uY2VydHxlbnwwfHx8fDE3ODc5Njk4OTh8MA&ixlib=rb-4.1.0&q=85";
export default function Browse() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [nowPlaying, setNowPlaying] = useState({});
  const [historyMap, setHistoryMap] = useState({});
  const [health, setHealth] = useState({});
  const [listeners, setListeners] = useState({});
  const [programs, setPrograms] = useState([]);
  const [heroSubtitle, setHeroSubtitle] = useState("Stream nonstop gospel, praise & worship stations. Tune in, save your favorites, request songs and join the live chat.");
  const { user } = useAuth();
  const brand = useBrand();
  const host = pickHost(brand.featured_hosts);
  const [liveActive, setLiveActive] = useState(false);
  useEffect(() => {
    const check = () => api.get("/live/status").then((r) => setLiveActive(!!r.data.active)).catch(() => {});
    check();
    const t = setInterval(check, 20000);
    return () => clearInterval(t);
  }, []);
  const onAir = liveActive;
  const nextShow = nextLive();
  const { playStation } = usePlayer();
  const navigate = useNavigate();

  const load = () => {
    api.get("/stations").then((res) => { setStations(res.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/programs").then((res) => setPrograms(res.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const [trendingSongs, setTrendingSongs] = useState([]);
  useEffect(() => { api.get("/trending-songs").then((r) => setTrendingSongs(r.data.songs || [])).catch(() => {}); }, []);

  const [spotlight, setSpotlight] = useState(null);
  useEffect(() => { api.get("/spotlight").then((r) => setSpotlight(r.data.spotlight || null)).catch(() => {}); }, []);

  useEffect(() => {
    const fetchNow = () => api.get("/stations/nowplaying").then((res) => {
      const map = {};
      Object.entries(res.data || {}).forEach(([id, v]) => { if (v && v.title) map[id] = { title: v.title, artwork: v.artwork }; });
      setNowPlaying(map);
    }).catch(() => {});
    fetchNow();
    const t = setInterval(fetchNow, 25000);
    const fetchHist = () => api.get("/stations/history?limit=3").then((res) => setHistoryMap(res.data || {})).catch(() => {});
    fetchHist();
    const ht = setInterval(fetchHist, 25000);
    api.get("/stations/health").then((res) => setHealth(res.data || {})).catch(() => {});
    const fetchListeners = () => api.get("/stations/listeners").then((res) => setListeners(res.data || {})).catch(() => {});
    fetchListeners();
    const lt = setInterval(fetchListeners, 20000);
    return () => { clearInterval(t); clearInterval(lt); clearInterval(ht); };
  }, []);

  const VIBES = {
    Worship: ["worship", "praise"],
    Contemporary: ["contemporary", "gospel mix"],
    Classic: ["classic", "southern"],
    Urban: ["urban"],
    African: ["african"],
    Christian: ["christian", "positive"],
  };
  const vibeOf = (s) => {
    const g = (s.genre || "").toLowerCase();
    for (const [vibe, keys] of Object.entries(VIBES)) {
      if (keys.some((k) => g.includes(k))) return vibe;
    }
    return "Gospel";
  };
  const presentVibes = Array.from(new Set(stations.map(vibeOf)));
  const filters = ["All", ...Object.keys(VIBES).filter((v) => presentVibes.includes(v)), ...(presentVibes.includes("Gospel") ? ["Gospel"] : [])];
  const isOffline = (s) => health[s.id] === false;
  const trendingId = Object.entries(listeners).reduce((best, [id, c]) => (c > 0 && c > (best.c || 0) ? { id, c } : best), {}).id || null;
  const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const _toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
  const _nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const _today = WEEKDAYS[new Date().getDay()];
  const currentProgram = programs.find((p) => p.day === _today && _nowMin >= _toMin(p.start_time) && (_nowMin < _toMin(p.end_time) || p.end_time === "00:00")) || null;
  const filtered = filter === "All" ? stations : stations.filter((s) => vibeOf(s) === filter);
  const visible = [...filtered].sort((a, b) => (isOffline(a) ? 1 : 0) - (isOffline(b) ? 1 : 0));

  const toggleFav = async (station) => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/favorites/${station.id}`);
      setStations((prev) => prev.map((s) => (s.id === station.id ? { ...s, is_favorite: data.favorite } : s)));
      toast.success(data.favorite ? "Added to favorites" : "Removed from favorites");
    } catch (e) { toast.error("Please sign in"); }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1030] via-[#0A1030]/70 to-[#0A1030]/30" />
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#E6B34A]/10 blur-3xl" />
          <div className="absolute -bottom-20 left-10 w-96 h-96 rounded-full bg-[#5B7CFA]/10 blur-3xl" />
        </div>
        <div className="relative px-4 sm:px-6 lg:px-12 pt-16 pb-12 max-w-3xl">
          <div className="flex items-center gap-4 mb-8" data-testid="index-logo">
            <img src={brand.logo} alt="In His Image Radio" className="w-20 h-20 object-contain logo-float" />
            <div>
              <p className="font-display font-extrabold text-2xl tracking-tight leading-none">In His Image Radio</p>
              <p className="overline mt-2" style={{ color: brand.brand_primary }}>Gospel · Praise · Worship</p>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
            <span className="w-2 h-2 rounded-full live-dot" style={{ backgroundColor: onAir ? "#FF3B30" : brand.brand_primary }} />
            <span className="overline">{onAir ? "Live show on air" : "Streaming 24/7"}</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]" data-testid="hero-heading">
            <HeroHeading text={brand.hero_heading} highlight={brand.hero_highlight} color={brand.brand_primary} />
          </h1>
          <p className="text-zinc-300 text-lg leading-relaxed mt-5 max-w-xl" data-testid="hero-subtitle">
            {brand.hero_subtitle}
          </p>
          {currentProgram && (
            <Link
              to="/schedule"
              data-testid="now-playing-show"
              className="mt-6 flex items-center gap-3 rounded-2xl border border-[#E6B34A]/30 bg-[#E6B34A]/[0.07] hover:bg-[#E6B34A]/[0.12] p-4 max-w-xl transition-colors"
            >
              <div className="w-11 h-11 rounded-full bg-[#E6B34A]/15 flex items-center justify-center shrink-0">
                <Mic className="w-5 h-5 text-[#E6B34A]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 text-[#FF7A70]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] live-dot" />
                    <span className="overline">On air now</span>
                  </span>
                </div>
                <p className="font-display font-extrabold text-lg tracking-tight truncate mt-0.5" data-testid="now-playing-title">{currentProgram.title}</p>
                {currentProgram.host && <p className="text-sm text-zinc-400 truncate">with {currentProgram.host} · until {currentProgram.end_time}</p>}
              </div>
            </Link>
          )}
          {user && stations[0] && (
            <button
              onClick={() => playStation(stations[0])}
              data-testid="hero-play"
              style={{ backgroundColor: brand.brand_primary }}
              className="mt-8 inline-flex items-center gap-3 px-7 py-4 rounded-full text-[#0A1030] font-bold hover:brightness-95 transition-all shadow-[0_0_40px_rgba(230,179,74,0.35)]"
            >
              <Play className="w-5 h-5" fill="currentColor" /> {brand.hero_cta}
            </button>
          )}

          {!user && (
            <div className="mt-8 fade-up rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 max-w-xl" data-testid="guest-welcome">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E6B34A]/10 text-[#E6B34A] mb-4">
                <Sparkles className="w-4 h-4" />
                <span className="overline">Welcome, friend</span>
              </div>
              <h3 className="font-display font-extrabold text-2xl tracking-tight leading-tight" data-testid="guest-card-title">
                <HeroHeading text={brand.guest_card_title} highlight={brand.guest_card_highlight} color={brand.brand_primary} />
              </h3>
              <p className="text-zinc-300 mt-2 text-sm leading-relaxed" data-testid="guest-card-body">
                {brand.guest_card_body}
              </p>
              <div className="grid grid-cols-3 gap-3 mt-5">
                <div className="flex flex-col items-center gap-1.5 text-center">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center"><Heart className="w-5 h-5 text-[#E6B34A]" /></div>
                  <span className="text-[11px] text-zinc-400">Save faves</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 text-center">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center"><Music4 className="w-5 h-5 text-[#E6B34A]" /></div>
                  <span className="text-[11px] text-zinc-400">Request songs</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 text-center">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center"><MessageSquareDot className="w-5 h-5 text-[#E6B34A]" /></div>
                  <span className="text-[11px] text-zinc-400">Live chat</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <Link to="/register" data-testid="guest-signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
                  Create free account
                </Link>
                <Link to="/login" data-testid="guest-signin" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 hover:bg-white/10 text-zinc-100 font-semibold transition-colors">
                  Sign in
                </Link>
              </div>
            </div>
          )}
          {/* Mobile host card */}
          <Link to="/meet-the-host" data-testid="hero-host-mobile" className="lg:hidden mt-8 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 hover:border-white/20 transition-colors">
            <div className="relative shrink-0">
              <div className="absolute -inset-1 rounded-2xl blur-md opacity-40" style={{ backgroundColor: onAir ? "#FF3B30" : brand.brand_primary }} />
              <img src={resolvePhoto(host.photo)} alt={host.name} data-testid="hero-host-photo-mobile" className="relative w-20 h-20 rounded-2xl object-cover object-top" />
            </div>
            <div className="min-w-0">
              {onAir ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold mb-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> Live now
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/10 text-[10px] font-bold mb-1" style={{ color: brand.brand_primary }}>
                  <Mic className="w-3 h-3" /> Your Host
                </span>
              )}
              <p className="font-display font-extrabold text-xl tracking-tight leading-tight truncate">{host.name}</p>
              <p className="text-sm text-zinc-300 truncate">{host.role}</p>
              <span className="text-xs font-bold mt-1 inline-block" style={{ color: brand.brand_primary }}>Meet the host →</span>
            </div>
          </Link>
        </div>

        <Link to="/meet-the-host" data-testid="hero-host" className="hidden lg:flex absolute right-0 bottom-0 top-24 w-[46%] items-end justify-center pb-6 pointer-events-auto group">
          {/* Stage backdrop so the host pops off the page */}
          <div className="absolute inset-y-0 right-0 w-full pointer-events-none" style={{ background: "linear-gradient(to left, rgba(10,16,48,0.75), transparent 70%)" }} />
          <div className="absolute right-16 bottom-10 w-[26rem] h-[26rem] rounded-full blur-[90px] pointer-events-none transition-opacity duration-700" style={{ backgroundColor: onAir ? "#FF3B30" : brand.brand_primary, opacity: onAir ? 0.55 : 0.6 }} />
          <div className={`absolute right-16 bottom-10 w-[26rem] h-[26rem] rounded-full blur-[90px] opacity-40 pointer-events-none ${onAir ? "host-liveglow" : ""}`} style={{ backgroundColor: brand.brand_primary }} />
          <div className="absolute right-24 bottom-24 w-64 h-64 rounded-full blur-3xl opacity-25 bg-white pointer-events-none" />
          <div className="absolute bottom-10 right-8 z-10 text-right">
            {onAir ? (
              <span data-testid="host-live-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF3B30] text-white text-xs font-bold mb-2 shadow-lg">
                <span className="w-2 h-2 rounded-full bg-white live-dot" /> Live now
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0A1030]/70 backdrop-blur border border-white/10 text-xs mb-2" style={{ color: brand.brand_primary }}>
                <Mic className="w-3.5 h-3.5" /> Your Host
              </span>
            )}
            <p className="font-display font-extrabold text-3xl tracking-tight leading-none drop-shadow-lg">{host.name}</p>
            <p className="text-sm text-zinc-200 mt-1">{host.role}</p>
            <span className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-[#0A1030] group-hover:brightness-95 transition-all" style={{ backgroundColor: brand.brand_primary }}>Meet the host →</span>
          </div>
          <img src={resolvePhoto(host.photo)} alt={host.name} data-testid="hero-host-photo" className="host-float relative z-[1] max-h-full w-auto object-contain object-bottom drop-shadow-[0_25px_50px_rgba(0,0,0,0.65)] group-hover:scale-[1.03] transition-transform duration-500" style={{ filter: `drop-shadow(0 0 28px ${(onAir ? "#FF3B30" : brand.brand_primary)}66)` }} />
        </Link>
      </section>

      {/* Next live show countdown */}
      <section className="px-4 sm:px-6 lg:px-12 pt-8" data-testid="home-countdown">
        {onAir ? (
          <Link to="/live" data-testid="home-live-now" className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-3xl border border-[#FF3B30]/30 bg-gradient-to-br from-[#2A1114] to-[#141C42] p-6 hover:border-[#FF3B30]/50 transition-colors">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FF3B30]" />
              </span>
              <div className="min-w-0">
                <p className="overline text-[#FF7A70]">On air right now</p>
                <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight leading-tight">{host.name} is live — tune in</h3>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FF3B30] text-white font-bold shrink-0"><Radio className="w-4 h-4" /> Listen live</span>
          </Link>
        ) : nextShow ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 rounded-3xl border border-[#E6B34A]/20 bg-gradient-to-br from-[#141C42] to-[#0C1338] p-6" data-testid="home-next-show">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-[#E6B34A]/15 flex items-center justify-center shrink-0"><Radio className="w-5 h-5 text-[#E6B34A]" /></div>
              <div className="min-w-0">
                <p className="overline text-[#E6B34A]">Next live show</p>
                <h3 className="font-display font-extrabold text-lg sm:text-xl tracking-tight leading-tight truncate">{nextShow.title} · {formatNextLive(nextShow)}</h3>
              </div>
            </div>
            <div className="rounded-2xl bg-[#0A1030]/60 border border-white/5 px-5 py-3 shrink-0">
              <Countdown target={nextShow.date} testid="home-countdown-timer" />
            </div>
            <Link to="/live" data-testid="home-countdown-cta" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors shrink-0">Set a reminder →</Link>
          </div>
        ) : null}
      </section>

      {/* Parent ministry banner */}
      <section className="px-4 sm:px-6 lg:px-12 pt-8" data-testid="ministry-banner">
        <div className="relative overflow-hidden rounded-3xl border border-[#E6B34A]/20 bg-gradient-to-br from-[#141C42] to-[#0C1338] p-6 sm:p-8">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#E6B34A]/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-2xl bg-[#E6B34A]/15 flex items-center justify-center shrink-0">
                <Church className="w-6 h-6 text-[#E6B34A]" />
              </div>
              <div className="min-w-0">
                <p className="overline text-[#E6B34A] mb-1">Our Parent Ministry</p>
                <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight leading-tight">In HIS Image Movement Ministries</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed max-w-xl">This station is a ministry of IHIMM — sharing the gospel on the airwaves. Learn more about the movement and support the mission.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a href="https://ihimm.org/give" target="_blank" rel="noopener noreferrer" data-testid="ministry-give" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
                <HandHeart className="w-4 h-4" /> Give
              </a>
              <a href="https://ihimm.org" target="_blank" rel="noopener noreferrer" data-testid="ministry-learn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 text-zinc-100 font-semibold transition-colors">
                Learn more <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stations */}
      {user && (
      <section className="px-4 sm:px-6 lg:px-12 pt-10 pb-32">
        {user && <GettingStarted />}

        {spotlight && (
          <a
            href={spotlight.link_url || undefined}
            target={spotlight.link_url ? "_blank" : undefined}
            rel="noopener noreferrer"
            data-testid="sponsor-spotlight"
            className={`group relative block mb-10 rounded-2xl overflow-hidden border border-[#E6B34A]/60 ring-1 ring-[#E6B34A]/40 bg-gradient-to-r from-[#1A1230] via-[#141C42] to-[#1A1230] shadow-[0_0_28px_rgba(230,179,74,0.28)] ${spotlight.link_url ? "cursor-pointer" : ""}`}
          >
            <div className="absolute top-0 left-0 flex items-center gap-1.5 px-3 py-1.5 rounded-br-2xl bg-[#E6B34A]">
              <Star className="w-3.5 h-3.5 text-[#0A1030]" fill="currentColor" />
              <span className="text-[11px] font-black tracking-wide text-[#0A1030] uppercase">Sponsor Spotlight</span>
            </div>
            <div className="flex items-center gap-5 p-6 pt-10">
              {spotlight.logo_url ? (
                <img src={`${API.replace("/api", "")}${spotlight.logo_url}`} alt={spotlight.business_name} className="w-20 h-20 rounded-2xl object-cover bg-white/5 ring-1 ring-[#E6B34A]/40 shrink-0" data-testid="spotlight-logo" />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-[#E6B34A]/15 flex items-center justify-center shrink-0"><Sparkles className="w-8 h-8 text-[#E6B34A]" /></div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-display font-extrabold text-xl lg:text-2xl tracking-tight truncate" data-testid="spotlight-name">{spotlight.business_name}</h3>
                {spotlight.tagline && <p className="text-sm text-zinc-300 mt-1 line-clamp-2">{spotlight.tagline}</p>}
                <p className="overline text-[#E6B34A] mt-2">Proudly supporting In His Image Radio</p>
              </div>
              {spotlight.link_url && (
                <span className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E6B34A] text-[#0A1030] font-bold text-sm shrink-0 group-hover:bg-[#CF9A34] transition-colors">
                  Visit <ExternalLink className="w-4 h-4" />
                </span>
              )}
            </div>
          </a>
        )}

        <FeaturedEvents />
        <TrendingEvents />

        {trendingSongs.length > 0 && (
          <div className="mb-10" data-testid="trending-songs">
            <div className="flex items-center gap-3 mb-4">
              <Flame className="w-5 h-5 text-[#FF6B35]" />
              <h2 className="font-display font-extrabold text-lg lg:text-2xl tracking-tight">Now Trending</h2>
              <span className="text-sm text-zinc-500">most-hearted this week</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {trendingSongs.map((t, i) => (
                <div key={i} data-testid={`trending-song-${i}`} className="shrink-0 w-44 rounded-2xl bg-[#141C42] border border-white/5 overflow-hidden">
                  <div className="relative h-28 bg-[#0F1943]">
                    {t.artwork ? <img src={t.artwork} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Music4 className="w-8 h-8 text-zinc-600" /></div>}
                    <span className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[#0A1030]/80 text-[#E6B34A] text-xs font-black flex items-center justify-center">{i + 1}</span>
                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold"><Heart className="w-3 h-3 fill-white" /> {t.count}</span>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-bold truncate">{t.song}</p>
                    <p className="text-xs text-zinc-400 truncate">{t.artist || t.station || "Gospel"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <Radio className="w-5 h-5 text-[#E6B34A]" />
          <h2 className="font-display font-extrabold text-lg lg:text-2xl tracking-tight">All Stations</h2>
          <span className="text-sm text-zinc-500">{visible.length}</span>
        </div>

        {!loading && filters.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="station-filters">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${filter === f ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
              >
                {f}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>
        ) : visible.length === 0 ? (
          <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="no-stations">No stations in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="stations-grid">
            {visible.map((s, i) => (
              <StationCard key={s.id} station={s} onToggleFav={toggleFav} index={i} nowPlaying={nowPlaying[s.id]} history={historyMap[s.id]} offline={isOffline(s)} listeners={listeners[s.id] || 0} trending={s.id === trendingId} />
            ))}
          </div>
        )}
      </section>
      )}
    </div>
  );
}
